import { useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { foldRefsToFindings, type FindingRefs } from './findingRefSets';
import { useFamilyDescriptions } from './useFamilyDescriptions';
import {
    planumRefLabel,
    profileRefLabel,
    useFindingRefResolver
} from './findingRefLabels';
import { locationLabel } from './locationLabel';
import FindingRefChip from './FindingRefChip';

import { compareNatural } from '../../utils/compare';
import { useFormReadOnly } from '../../components/formReadOnly';
import { chipClipboardProps } from '../../components/chipClipboard';

type RefKind = 'planum' | 'profile' | 'finding';

interface RefOption {
    /** `${kind}:${id}` — unique across kinds. */
    key: string;
    kind: RefKind;
    id: string;
    /** "Bef. 7000, Fl. 1, Pl. 1" — same Lage line as the stratigraphy fields. */
    label: string;
    /** Route to the referenced record's editor (chips link there). */
    to?: string;
}

interface FindingDescriptionPickerProps {
    /** The project whose Teilmaßnahme family's Befunde are offered. */
    projectId: string;
    value: FindingRefs;
    onChange: (next: FindingRefs) => void;
    label: string;
    /** Hide this Befund's own descriptions (e.g. "identisch mit" can't be self). */
    excludeFindingId?: string;
    /** Offer only Planum/Profil descriptions — no bare whole-Befund options. */
    descriptionsOnly?: boolean;
    /**
     * Offer only WHOLE Befunde — the mirror image of `descriptionsOnly`, for a
     * record that covers Befunde as units (the Gesamtbeschreibung). A selection
     * only ever lands in `findingIds`. Refs stored per description by an earlier
     * version are folded up to their Befund on read.
     */
    wholeFindingsOnly?: boolean;
    /** Cap the selection at ONE ref — picking another replaces it (Funde). */
    single?: boolean;
    /**
     * Open a referenced record's editor from its chip. Pass a GUARDED navigate
     * (useGuardedNavigate) — the picker lives inside an editor; omit to make the
     * chips non-linking.
     */
    onOpenRef?: (to: string) => void;
}

/**
 * A multi-select over the family's Befund DESCRIPTIONS. Each Befund's Planum and
 * Profil rows are SEPARATE options ("12; Fläche A; Profil 1", "…Profil 3"), so a
 * record pins the exact description — Profil 1 without dragging in Profil 3. A
 * Befund with no description yet is offered as a bare "12". Selections split
 * into `planumIds` / `profileIds` / `findingIds`.
 *
 * With `wholeFindingsOnly` the list is Befunde instead, everything landing in
 * `findingIds`.
 */
export default function FindingDescriptionPicker({
    projectId,
    value,
    onChange,
    label,
    excludeFindingId,
    descriptionsOnly = false,
    wholeFindingsOnly = false,
    single = false,
    onOpenRef
}: FindingDescriptionPickerProps) {
    // Follows the surrounding form (see formReadOnly).
    const readOnly = useFormReadOnly();
    const { findings, planums, profiles } = useFamilyDescriptions(projectId);

    const numberById = useMemo(
        () =>
            new Map(
                findings.map((finding) => [finding.id, finding.findingNumber])
            ),
        [findings]
    );
    const refResolver = useFindingRefResolver(findings, planums, profiles);

    const options = useMemo<RefOption[]>(() => {
        const out: RefOption[] = [];
        // One option per Befund, no Planum/Profil rows at all.
        if (wholeFindingsOnly) {
            for (const finding of findings) {
                if (finding.id === excludeFindingId || !finding.findingNumber) {
                    continue;
                }
                out.push({
                    key: `finding:${finding.id}`,
                    kind: 'finding',
                    id: finding.id,
                    label: locationLabel({
                        findingNumber: finding.findingNumber
                    })
                });
            }
            return out.sort((firstOption, secondOption) =>
                compareNatural(firstOption.label, secondOption.label)
            );
        }
        for (const planum of planums) {
            if (planum.findingId === excludeFindingId) {
                continue;
            }
            const number = numberById.get(planum.findingId);
            if (!number) {
                continue;
            }
            out.push({
                key: `planum:${planum.id}`,
                kind: 'planum',
                id: planum.id,
                label: planumRefLabel(number, planum)
            });
        }
        for (const profile of profiles) {
            if (profile.findingId === excludeFindingId) {
                continue;
            }
            const number = numberById.get(profile.findingId);
            if (!number) {
                continue;
            }
            out.push({
                key: `profile:${profile.id}`,
                kind: 'profile',
                id: profile.id,
                label: profileRefLabel(number, profile)
            });
        }
        if (descriptionsOnly) {
            return out.sort((firstOption, secondOption) =>
                compareNatural(firstOption.label, secondOption.label)
            );
        }
        // Bare Befunde with no description yet stay selectable as whole-Befund refs.
        const describedIds = new Set([
            ...planums.map((planum) => planum.findingId),
            ...profiles.map((profile) => profile.findingId)
        ]);
        for (const finding of findings) {
            if (finding.id === excludeFindingId) {
                continue;
            }
            if (describedIds.has(finding.id) || !finding.findingNumber) {
                continue;
            }
            out.push({
                key: `finding:${finding.id}`,
                kind: 'finding',
                id: finding.id,
                label: finding.findingNumber
            });
        }
        return out.sort((firstOption, secondOption) =>
            compareNatural(firstOption.label, secondOption.label)
        );
    }, [
        planums,
        profiles,
        findings,
        numberById,
        excludeFindingId,
        descriptionsOnly,
        wholeFindingsOnly
    ]);

    const optionByKey = useMemo(
        () => new Map(options.map((option) => [option.key, option])),
        [options]
    );

    // Stored ids back to option objects. A target that isn't loaded or was
    // deleted falls back to a minimal label, so a ref is never silently lost.
    const selected = useMemo<RefOption[]>(() => {
        /**
         * Whole-Befund mode reads a per-description ref as its Befund. Only
         * DISPLAY folds — what is stored stays untouched until the user edits
         * the selection, so nothing is lost while the family's descriptions are
         * still loading.
         */
        const refs: { kind: RefKind; id: string }[] = wholeFindingsOnly
            ? foldRefsToFindings(value, planums, profiles).findingIds.map(
                  (id) => ({ kind: 'finding' as const, id })
              )
            : [
                  ...value.planumIds.map((id) => ({
                      kind: 'planum' as const,
                      id
                  })),
                  ...value.profileIds.map((id) => ({
                      kind: 'profile' as const,
                      id
                  })),
                  ...value.findingIds.map((id) => ({
                      kind: 'finding' as const,
                      id
                  }))
              ];
        return refs.map(({ kind, id }) => {
            const to = refResolver.get(id)?.to;
            const key = `${kind}:${id}`;
            const known = optionByKey.get(key);
            if (known) {
                return { ...known, to };
            }
            return { key, kind, id, label: numberById.get(id) ?? id, to };
        });
    }, [
        value,
        optionByKey,
        numberById,
        refResolver,
        wholeFindingsOnly,
        planums,
        profiles
    ]);

    // A pick and a paste land alike.
    function commit(next: readonly RefOption[]) {
        // Single mode (Funde): a new pick replaces the current ref.
        const chosen =
            single && next.length > 1 ? [next[next.length - 1]] : next;
        const result: FindingRefs = {
            planumIds: [],
            profileIds: [],
            findingIds: []
        };
        for (const option of chosen) {
            if (option.kind === 'planum') {
                result.planumIds.push(option.id);
            } else if (option.kind === 'profile') {
                result.profileIds.push(option.id);
            } else {
                result.findingIds.push(option.id);
            }
        }
        onChange(result);
    }

    return (
        <Autocomplete
            multiple
            readOnly={readOnly}
            filterSelectedOptions
            options={options}
            value={selected}
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.label
            }
            isOptionEqualToValue={(option, candidate) =>
                option.key === candidate.key
            }
            /**
             * The listbox keys on the option's IDENTITY, not on its label. Two
             * descriptions can read exactly alike — the same Profilnummer
             * described twice without a Teil mark, which an import can produce.
             * Without this, MUI falls back to the label, React sees two children
             * with one key and may reuse the wrong row while the list filters or
             * scrolls — and the picker then writes the wrong relation.
             */
            renderOption={(optionProps, option) => {
                const { key: _labelKey, ...rest } = optionProps;
                return (
                    <li {...rest} key={option.key}>
                        {option.label}
                    </li>
                );
            }}
            renderValue={(selectedOptions, getItemProps) =>
                selectedOptions.map((option, index) => {
                    const { key, ...itemProps } = getItemProps({ index });
                    return (
                        <FindingRefChip
                            key={key}
                            {...itemProps}
                            label={option.label}
                            to={option.to}
                            onOpen={onOpenRef}
                        />
                    );
                })
            }
            onChange={(_event, next) => commit(next)}
            {...chipClipboardProps({
                value: selected,
                getLabel: (option: RefOption) => option.label,
                options,
                onChange: commit,
                readOnly
            })}
            renderInput={(params) => <TextField {...params} label={label} />}
        />
    );
}
